import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import html2canvas from 'html2canvas';
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { jsPDF } from "jspdf";
import { PlusCircle, FileEdit, XCircle, Printer, Send } from "lucide-react";
import { getSessionUsername } from "../../utils/sessionUser";
/* ---------------- ICONS ---------------- */
const TimesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

/* ---------------- MODAL ---------------- */
const Modal = ({ title, isOpen, onClose, children }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full p-4 overflow-y-auto bg-black/50">
      <div className="relative w-full max-w-lg p-6 mx-auto overflow-auto bg-white rounded-lg shadow-lg max-h-[90vh]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">{title}</h3>
          <button onClick={onClose} aria-label="Close modal">
            <TimesIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ---------------- BUTTON ---------------- */
const Button = ({
  children,
  onClick,
  type = "button",
  disabled = false,
  className = "",
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
    >
      {children}
    </button>
  );
};
const today = new Date().toISOString().split("T")[0]; // yyyy-mm-dd
const normalizeMode = (mode) => String(mode || "").trim().toUpperCase();
const getDefaultMode = (paymentModes) => paymentModes[0]?.value || "";
const normalizePaymentModes = (data) => {
  const table2 = data?.Table2 ?? [];
  if (table2.length > 0) {
    return table2
      .map((item) => ({
        label: item.PAYMODE,
        value: normalizeMode(item.PAYMODE),
        bankId: item.bANKID ?? item.BANKID ?? item.BankId ?? "",
      }))
      .filter((item) => item.value);
  }

  return (data?.Table1 ?? [])
    .map((item) => ({
      label: item.Mode,
      value: normalizeMode(item.Mode),
      bankId: item.bANKID ?? item.BANKID ?? item.BankId ?? "",
    }))
    .filter((item) => item.value);
};

const ReceiptForm = ({ isOpen, onClose, onSave, mode, initialData }) => {
  const [formData, setFormData] = useState({
    Vrid: "",
    Vrno: "",
    VrDate: today,
    Vid: "",
    VName: "",
    VCode: "",
    Amount: "", // Amount stays empty initially
    Mode: "",
    Chqno: "",
    Notes: "",
    Balance: 0, // Prev.Balance
  });
  const [customers, setCustomers] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);


  function toDateOnly(isoString = "") {
    // guard against null/undefined
    return isoString ? isoString.split("T")[0] : "";
  }

  const username = getSessionUsername();
  const resetForm = useCallback(() => {
    const defaultMode = getDefaultMode(paymentModes);
    setFormData({
      Vrid: "",
      Vrno: "",
      VrDate: today,
      Vid: "",
      VName: "",
      VCode: "",
      Amount: "",
      Mode: defaultMode,
      Chqno: "",
      Notes: "",
      Balance: 0,
    });
    setCustomerSearch("");
    setIsDropdownOpen(false);
  }, [paymentModes]);

  useEffect(() => {
    if (isOpen && mode === "add") {
      resetForm();
    }
  }, [isOpen, mode, resetForm]);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData(prev => ({
        ...prev,
        Vrid: initialData.Vrid,
        Vrno: initialData.Vrno,
        // 🔑 Convert here:
        VrDate: initialData.VrDate
          ? toDateOnly(initialData.VrDate)
          : today,
        Vid: initialData.VId,
        VName: initialData.VName,
        VCode: initialData.VCode,
        Amount: initialData.CDue,
        Mode: normalizeMode(initialData.Mode) || getDefaultMode(paymentModes),
        Chqno: initialData.ChequeNo,
        Notes: initialData.Remark,
        Balance: initialData.Balance,
      }));
    }
  }, [mode, initialData, paymentModes]);
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const data = await api.CustomerListWithBalance(username);
        const customersTable = data?.Table ?? [];
        const modes = normalizePaymentModes(data);
        setCustomers(customersTable);
        setPaymentModes(modes);

        if (mode === "edit" && initialData) {
          const customer = customersTable.find((c) => c.Vid === initialData.VId);
          if (customer) setCustomerSearch(customer.Vname);

          setFormData({
            Vrid: initialData.Vrid,
            Vrno: initialData.Vrno,
            VrDate: initialData.VrDate
              ? toDateOnly(initialData.VrDate)
              : today,
            Vid: initialData.VId,
            VName: initialData.VName,
            VCode: initialData.VCode,
            Amount: initialData.VrNetTotal || "", // Keep Amount from existing data
            Mode: normalizeMode(initialData.Mode) || getDefaultMode(modes),
            Chqno: initialData.ChequeNo,
            Notes: initialData.Remark,
            Balance: initialData.Balance || 0,
          });
        } else if (mode === "add") {
          const defaultMode = getDefaultMode(modes);
          // RESET completely on "New"
          setFormData({
            Vrid: "",
            Vrno: "",
            VrDate: today,
            Vid: "",
            VName: "",
            VCode: "",
            Amount: "",
            Mode: defaultMode,
            Chqno: "",
            Notes: "",
            Balance: 0,
          });
          setCustomerSearch(""); // Clear search
        }
      } catch (err) {
        console.error("Failed to fetch customers:", err);
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, [mode, initialData, username]);

  const filteredCustomers = customers.filter((c) =>
    c.Vname.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleCustomerSelect = (customer) => {
    setFormData((prev) => ({
      ...prev,
      Vid: customer.Vid,
      VName: customer.Vname,
      VCode: customer.VCode,
      Balance: customer.Balance, // Only bind Prev.Balance
    }));
    setCustomerSearch(customer.Vname);
    setIsDropdownOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "Amount" && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(formData.Amount || "").trim()) {
      alert("Please enter amount.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...(() => {
          const paymentMode = paymentModes.find((item) => item.value === normalizeMode(formData.Mode));
          return {
            BankId: Number(paymentMode?.bankId || 0),
            BANKID: Number(paymentMode?.bankId || 0),
            bANKID: Number(paymentMode?.bankId || 0),
            PAYMODE: paymentMode?.label || formData.Mode,
          };
        })(),
        SqlAction: mode === "add" ? "Insert" : "Update",
        ...formData,
        Mode: normalizeMode(formData.Mode),
        Vid: Number(formData.Vid),
        Amount: Number(formData.Amount),
        VrDate: formData.VrDate,
        User: username,
        VrId: mode === "edit" ? initialData.Vrid : null,
      };
      await api.SavePayment(payload);
      await onSave();
      onClose();
    } catch (err) {
      console.error("Failed to save receipt:", err);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Modal title={mode === "add" ? "Add Receipt" : "Edit Receipt"} isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Customer dropdown */}
        <div className="relative">
          <label className="block mb-1 text-sm font-medium">Customer</label>
          <input
            type="text"
            value={customerSearch}
            onChange={(e) => {
              setCustomerSearch(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            className="w-full p-2 border rounded"
            placeholder="Search customer..."
          />
          {isDropdownOpen && filteredCustomers.length > 0 && (
            <ul className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded shadow max-h-40">
              {filteredCustomers.map((c) => (
                <li
                  key={c.Vid}
                  onClick={() => handleCustomerSelect(c)}
                  className="p-2 cursor-pointer hover:bg-gray-100"
                >
                  {c.Vname} — Bal: ₹{c.Balance}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Prev.Balance */}
        <div>
          <label className="block mb-1 text-sm font-medium">Prev.Balance</label>
          <input
            type="text"
            value={`₹${(formData.Balance || 0).toFixed(2)}`}
            readOnly
            className="w-full p-2 bg-gray-100 border rounded"
          />
        </div>
        {/* Receipt Date */}
        <div>
          <label className="block mb-1 text-sm font-medium">Receipt Date</label>
          <input
            type="date"
            name="VrDate"
            value={formData.VrDate}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
        </div>

        {/* Mode */}
        <div>
          <label className="block mb-1 text-sm font-medium">Mode</label>
          <select name="Mode" value={formData.Mode} onChange={handleChange} className="w-full p-2 border rounded">
            {paymentModes.map((paymentMode) => (
              <option key={paymentMode.value} value={paymentMode.value}>
                {paymentMode.label}
              </option>
            ))}
          </select>
        </div>

        {normalizeMode(formData.Mode) === "CHEQUE" && (
          <div>
            <label className="block mb-1 text-sm font-medium">Cheque No</label>
            <input type="text" name="Chqno" value={formData.Chqno} onChange={handleChange} className="w-full p-2 border rounded" />
          </div>
        )}

        {/* Amount */}
        <div>
          <label className="block mb-1 text-sm font-medium">Amount</label>
          <input
            type="text"
            inputMode="decimal"
            name="Amount"
            value={formData.Amount}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block mb-1 text-sm font-medium">Notes</label>
          <textarea name="Notes" value={formData.Notes} onChange={handleChange} className="w-full p-2 border rounded" />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={isSaving} className="px-4 py-2 text-white bg-blue-600 rounded">
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------------- RECEIPT VIEW ---------------- */
const ReceiptView = ({ isOpen, onClose, receipt }) => {
  if (!receipt) return null;

  return (
    <Modal title="Receipt" isOpen={isOpen} onClose={onClose}>
      <div className="space-y-2">
        <div>
          <strong>Customer:</strong> {receipt.VName}
        </div>
        <div>
          <strong>Date:</strong>{" "}
          {new Date(receipt.VrDate).toLocaleDateString()}
        </div>
        <div>
          <strong>Amount:</strong> ₹{Number(receipt.VrNetTotal).toFixed(2)}
        </div>
        <div>
          <strong>Mode:</strong> {receipt.Mode}
        </div>
        {receipt.ChequeNo && (
          <div>
            <strong>Cheque No:</strong> {receipt.ChequeNo}
          </div>
        )}
        <div>
          <strong>Notes:</strong> {receipt.Remark}
        </div>
      </div>
    </Modal>
  );
};

/* ---------------- MAIN PAGE ---------------- */
function ReceiptsPage({ dateWise = false }) {
  const today = new Date().toISOString().split("T")[0];
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [currentReceipt, setCurrentReceipt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [receiptHtml, setReceiptHtml] = useState("");
  const [receiptOrder, setReceiptOrder] = useState(null);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const username = getSessionUsername();

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = dateWise
        ? await api.GetPaymentApiDateWise(username, fromDate, toDate)
        : await api.GetPaymentlistApi(username);

      // console.log("Raw API response:", data);

      // Bind data correctly
      const receiptsData = (data?.Table || []).map((r) => ({
        Vrid: r.Vrid,
        Vrno: r.Vrno || "-",
        VrDate: r.VrDate ? new Date(r.VrDate) : null,
        VId: r.VId || 0,
        VName: r.VName || "-",
        VCode: r.VCode || "-",
        VrNetTotal: r.VrNetTotal || 0,
        Mode: r.Mode || "",
        ChequeNo: r.ChequeNo || "",
        Remark: r.Remark || "",
      }));

      // console.log("Mapped receiptsData:", receiptsData);

      setReceipts(receiptsData);
    } catch (err) {
      console.error("Error fetching receipts:", err);
      setError("Failed to load receipts");
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [dateWise, fromDate, toDate, username]);
  const handleReceiptClick = async (vrid, mobileNo) => {
    try {
      const resp = await api.get(`GetHtmlReceiptAPI?Vrid=${vrid}`);
      console.log("API response:", resp);

      const content = resp?.Table?.[0]?.PrintReceipt; // ✅ Correct path

      if (content) {
        setReceiptHtml(content);
        setReceiptOrder({ MobileNo: mobileNo });
        setIsReceiptOpen(true);
      } else {
        alert("Receipt content not found.");
        console.warn("Receipt content missing in API response:", resp);
      }
    } catch (err) {
      console.error("Error fetching receipt:", err);
      alert("Failed to fetch receipt.");
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handleAddClick = async () => {
    try {
      await api.CustomerListWithBalance(username);
    } catch (err) {
      console.error("Failed to fetch customers on new receipt:", err);
    }
    setFormMode("add");
    setCurrentReceipt(null);
    setIsFormOpen(true);
  };

  const handleEditClick = async (vrid) => {
    try {
      const data = await api.EditPaymentAPI(vrid);



      const table = data?.Table ?? []; // <-- use data.Table
      if (table.length === 0) {
        // console.error("No receipt data found for vrid:", vrid);
        setError("No receipt data found");
        return;
      }

      setCurrentReceipt(table[0]);
      setFormMode("edit");
      setIsFormOpen(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch receipt for editing");
    }
  };
  const PrintPreview = ({ htmlContent, order, onClose }) => {
    const hiddenRef = useRef(null);
    const [waMenuOpen, setWaMenuOpen] = useState(false);
    useEffect(() => {
      const handleKeys = (e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "F6") { e.preventDefault(); handlePrint(); }
        if (e.key === "F8") { e.preventDefault(); setWaMenuOpen((p) => !p); }
      };
      window.addEventListener("keydown", handleKeys);
      return () => window.removeEventListener("keydown", handleKeys);
    }, [onClose]);
    const handlePrint = () => {
      const iframe = document.getElementById("print-iframe");
      if (iframe?.contentWindow) iframe.contentWindow.print();
    };

    /* ---------- WhatsApp Share Helpers ---------- */
    const shareAsImage = async () => {
      setWaMenuOpen(false);
      if (!hiddenRef.current) return alert("Content not ready");
      const mobile = (order.MobileNo || "").replace(/[^0-9]/g, "");
      if (!mobile) return alert("Mobile number not found");

      try {
        const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        if (blob && navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
          alert("Image copied! Paste in WhatsApp chat.");
        }
        window.open(`https://wa.me/91${mobile}`, "_blank");
      } catch (err) {
        console.error("Image share failed:", err);
      }
    };

    const shareAsPDF = async () => {
      setWaMenuOpen(false);
      if (!hiddenRef.current) return alert("Content not ready");
      try {
        const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
        const doc = new jsPDF("p", "mm", "a4");
        const img = canvas.toDataURL("image/png");
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        doc.addImage(img, "PNG", 0, 0, pdfWidth, pdfHeight);
        const pdfBlob = doc.output("blob");
        const file = new File([pdfBlob], "receipt.pdf", { type: "application/pdf" });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Receipt PDF" });
        } else {
          const url = URL.createObjectURL(pdfBlob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
      } catch (err) {
        console.error("PDF share failed:", err);
      }
    };
    return (
      <>
        {/* Hidden copy for html2canvas */}
        <div
          ref={hiddenRef}
          style={{ position: "fixed", top: "-9999px", left: "-9999px", width: "500px", background: "#fff" }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />

        {/* Modal overlay */}
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="relative w-full max-w-2xl p-4 bg-white rounded-lg shadow-2xl">
            <h3 className="py-3 text-lg font-semibold text-center border-b">Receipt Preview</h3>

            <div className="px-4 py-2 border-b">
              <iframe
                id="print-iframe"
                srcDoc={htmlContent}
                title="Print Preview"
                style={{ width: "100%", height: "60vh", border: "none" }}
              />
            </div>

            <div className="flex justify-end gap-4 p-5">
              <Button
                onClick={onClose}
                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg shadow-md hover:from-red-600 hover:to-red-700"
              >
                <XCircle size={16} /> Close
              </Button>

              <Button
                onClick={handlePrint}
                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700"
              >
                <Printer size={16} /> Print (F6)
              </Button>

              <div className="relative">
                <Button
                  onClick={() => setWaMenuOpen((p) => !p)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-lg shadow-md hover:bg-green-600"
                >
                  <Send size={16} /> WhatsApp (F8)
                </Button>

                {waMenuOpen && (
                  <div className="absolute right-0 z-50 w-40 mt-2 bg-white border rounded-lg shadow-lg">
                    <button
                      onClick={shareAsImage}
                      className="block w-full px-3 py-2 text-sm text-left hover:bg-green-50"
                    >
                      📷 Share as Image
                    </button>
                    <button
                      onClick={shareAsPDF}
                      className="block w-full px-3 py-2 text-sm text-left hover:bg-blue-50"
                    >
                      📄 Share as PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };


  // ✅ unify property to avoid undefined errors
  const handleViewClick = async (vrid) => {
    try {
      const data = await api.EditPaymentAPI(vrid);
      const table = data?.Table ?? []; // <-- unified
      if (table.length === 0) {
        setError("No receipt data found");
        return;
      }
      setViewReceipt(table[0]);
      setIsViewOpen(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch receipt for viewing");
    }
  };
  const filtered = receipts.filter(
    (r) =>
      (r.VName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.Vrno || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full min-h-screen bg-slate-50">
      {/* Header */}
      <div className="px-2 py-2 bg-white border-b shadow-sm border-slate-200">
        {dateWise ? (
          <div className="relative mb-2 flex flex-col gap-2 sm:min-h-[48px] sm:justify-end">
            <h1 className="text-xl font-semibold tracking-normal text-center text-slate-800 sm:text-2xl">
              Datewise Receipt
            </h1>
            <div className="flex items-end justify-end gap-2 sm:absolute sm:right-0 sm:top-0">
              <div>
                <label className="block text-xs font-medium text-slate-500">From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-36 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500">To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-36 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700"
                />
              </div>
            </div>
          </div>
        ) : (
          <Heading>Customer Receipts</Heading>
        )}

        <div className="flex items-center justify-between mt-1">
          <Button
            className="flex items-center gap-2 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700"
            onClick={handleAddClick}
          >
            <PlusCircle size={18} /> New
          </Button>

          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("card")}
              className={`rounded-md p-2 ${viewMode === "card"
                ? "bg-teal-50 text-teal-700 shadow-sm"
                : "text-slate-500 hover:bg-slate-100"
                }`}
            >
              {/* Card icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z"
                />
              </svg>
            </button>

            <button
              onClick={() => setViewMode("table")}
              className={`rounded-md p-2 ${viewMode === "table"
                ? "bg-teal-50 text-teal-700 shadow-sm"
                : "text-slate-500 hover:bg-slate-100"
                }`}
            >
              {/* Table icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className={dateWise ? "flex items-center gap-2 border-b border-slate-200 bg-white px-1 py-1" : "flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2"}>
        <SearchInput
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* List */}
      <div className={dateWise ? "p-1" : "p-3"}>
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-red-600">{error}</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-sm text-center text-slate-500">No receipts found.</div>
        ) : viewMode === "table" ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Customer
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Mobile
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-right text-gray-900 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-center text-gray-900 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((r) => (
                  <tr key={r.Vrid}>
                    <td className="px-4 py-2">{r.VName}</td>
                    <td className="px-4 py-2">{r.VCode}</td>
                    <td className="px-4 py-2">
                      {new Date(r.VrDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      ₹{Number(r.VrNetTotal).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          onClick={() => handleEditClick(r.Vrid)}
                          className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white 
        bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
        hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
        transition-all duration-300 ease-in-out"
                        >
                          <FileEdit className="w-5 h-5" />
                          Edit
                        </Button>

                        <Button
                          onClick={() => handleReceiptClick(r.Vrid, r.VCode)}
                          className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white
        bg-green-500 rounded-lg shadow-md hover:bg-green-600 transition-all duration-300"
                        >
                          Receipt
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <div
                key={r.Vrid}
                className="p-3 bg-white border rounded-lg shadow-sm border-slate-200"
              >
                <div className="text-sm text-gray-700">
                  <strong>Customer:</strong> {r.VName}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Mobile:</strong> {r.VCode}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Date:</strong>{" "}
                  {new Date(r.VrDate).toLocaleDateString()}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Amount:</strong> ₹{Number(r.VrNetTotal).toFixed(2)}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={() => handleEditClick(r.Vrid)}
                    className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white 
      bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
      hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
      transition-all duration-300 ease-in-out"
                  >
                    <FileEdit className="w-5 h-5" />
                    Edit
                  </Button>

                  <Button
                    onClick={() => handleReceiptClick(r.Vrid, r.VCode)}
                    className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white
      bg-green-500 rounded-lg shadow-md hover:bg-green-600 transition-all duration-300"
                  >
                    Receipt
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReceiptForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={fetchReceipts}
        mode={formMode}
        initialData={currentReceipt}
      />

      <ReceiptView
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        receipt={viewReceipt}
      />
      {isReceiptOpen && (
        <PrintPreview
          htmlContent={receiptHtml}
          order={receiptOrder}
          onClose={() => setIsReceiptOpen(false)}
        />
      )}
    </div>
  );
}

export default ReceiptsPage;

